import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  CircularProgress,
  InputAdornment,
  Box
} from '@mui/material';
import {
  Percent as PercentIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useModal } from '../../../contexts/ModalContext';
import { useBookingDetails } from '../../../contexts/BookingDetailsContext';
import { numberToWords } from '../../../utils/helper';
import AgentSelect from '../../../components/common/AgentSelect';
import { formatCurrencyNoDecimals } from '../bms_helper';

const EditAgentIncentive = ({ open, onClose, booking, incentive, onSuccess, agents }) => {
  const { openErrorModal } = useModal();
  const { updateBookingAgentIncentive } = useBookingDetails();
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    agent_id: '',
    amount: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState({});


  // Populate form when incentive prop changes
  useEffect(() => {
    if (incentive && open) {
      setFormData({
        agent_id: incentive.agent?.id || '',
        amount: incentive.amount ? incentive.amount.toString() : '',
        description: incentive.description || ''
      });
      setFormErrors({});
    }
  }, [incentive, open]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleAgentChange = (agentId) => {
    setFormData(prev => ({
      ...prev,
      agent_id: agentId
    }));
    
    if (formErrors.agent_id) {
      setFormErrors(prev => ({
        ...prev,
        agent_id: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.agent_id) {
      errors.agent_id = 'Agent is required';
    }
    
    if (!formData.amount) {
      errors.amount = 'Incentive value is required';
    } else {
      const value = parseFloat(formData.amount);
      if (isNaN(value) || value <= 0) {
        errors.amount = 'Incentive value must be greater than 0';
      }
      
      // Check if percentage exceeds 100%
      const totalCommission = booking?.commission_breakdown?.total_commission || 0;
      if (totalCommission > 0 && value > totalCommission) {
        errors.amount = 'Incentive amount cannot exceed total commission';
      }
    }
    
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        agent_id: formData.agent_id,
        amount: parseFloat(formData.amount),
        description: formData.description.trim()
      };
      
      await updateBookingAgentIncentive(booking.id, incentive.id, payload);
      
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      openErrorModal('Error', error.message || 'Failed to update agent incentive');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      agent_id: '',
      amount: '',
      description: ''
    });
    setFormErrors({});
    onClose();
  };

  if (!incentive) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon color="primary" />
          <Typography variant="h6">Edit Agent Incentive</Typography>
        </Box>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={3}>
            {/* Row 1: Agent and Amount side by side */}
            <Grid item xs={12} md={6}>
              <AgentSelect
                label="Agent"
                value={formData.agent_id}
                onChange={handleAgentChange}
                agents={agents}
                disabled // Agent cannot be changed in edit mode
                error={!!formErrors.agent_id}
                helperText={formErrors.agent_id}
                required
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Amount"
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleFormChange}
                error={!!formErrors.amount}
                helperText={
                  formErrors.amount || 
                  (formData.amount && 
                    `${numberToWords(parseInt(formData.amount))} rupees only`
                  )
                }
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  inputProps: {
                    min: 0,
                    step: 1
                  }
                }}
              />
            </Grid>

            {/* Row 2: Description full width */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                multiline
                rows={3}
                error={!!formErrors.description}
                helperText={formErrors.description || 'Describe the incentive arrangement'}
                placeholder="e.g., Performance bonus for closing the deal, Referral incentive, etc."
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{ position: 'relative' }}
          >
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              'Update Incentive'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditAgentIncentive;
